// Column.jsx
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import TaskCard from "../organisms/TaskCard";

export default function Column({
  id,
  title,
  tasks,
  users,
  status,
  color = "#4db1e0",
  count = 0,
  isMobileView = false,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [wasDropped, setWasDropped] = useState(false);

  // Get only tasks for this column - memoized for performance
  const columnTasks = useMemo(
    () => tasks.filter((task) => task.status === status),
    [tasks, status]
  );

  // Generate column styles based on color prop
  const getColumnHeaderStyle = useCallback(() => {
    return {
      backgroundColor: color,
      borderColor: color,
    };
  }, [color]);

  // Check if this is an archive column
  const isArchiveColumn = status === "Archived";

  // Configure droppable zone - archive column is droppable but has special styling
  const { setNodeRef, isOver, active } = useDroppable({
    id,
    data: {
      type: "column",
      status,
      accepts: ["task"],
      columnId: id,
      title,
    },
  });

  // Flash effect when item is dropped
  useEffect(() => {
    if (isOver && active) {
      // Prepare for potential drop
    } else if (!isOver && wasDropped) {
      // Reset after animation completes
      const timeout = setTimeout(() => {
        setWasDropped(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [isOver, active, wasDropped]);

  // Memoize sortable items for performance
  const sortableItems = useMemo(
    () => columnTasks.map((task) => task.id),
    [columnTasks]
  );

  // Handlers for mouse events
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <div
      className={`flex flex-col flex-shrink-0 h-full w-80 rounded-lg bg-[#1a1e21] transition-all duration-150 ${
        isArchiveColumn ? "opacity-80" : ""
      } ${
        isOver
          ? "ring-2 ring-[#4db1e0] ring-opacity-70 shadow-lg transform scale-[1.01]"
          : wasDropped
            ? "ring-1 ring-[#4db1e0] ring-opacity-50 shadow-md"
            : isHovered
              ? "shadow-md ring-1 ring-[#4db1e0]/30"
              : ""
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`p-3 rounded-t-lg border-b ${isArchiveColumn ? "opacity-80" : ""}`}
        style={getColumnHeaderStyle()}
      >
        <div className="flex justify-between items-center">
          <h3 className="font-semibold text-white flex items-center">
            {isArchiveColumn && (
              <span className="mr-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                  />
                </svg>
              </span>
            )}
            {title}
          </h3>
          <span className="px-2.5 py-0.5 rounded-full text-sm font-medium bg-[#0f1418]/50 text-white">
            {count}
          </span>
        </div>
      </div>

      <div
        ref={setNodeRef}
        className={`flex-1 p-2 overflow-y-auto bg-[#1a1e21] rounded-b-lg ${
          isOver
            ? "bg-[#262b30] border-2 border-dashed border-[#4db1e0]/50 shadow-inner shadow-[#4db1e0]/20"
            : ""
        } ${wasDropped ? "bg-[#262b30]/50" : ""} ${isArchiveColumn ? "bg-[#1a1e21]/90" : ""}`}
        style={{
          minHeight: "calc(100vh - 220px)",
          maxHeight: "calc(100vh - 220px)",
        }}
      >
        <SortableContext
          items={sortableItems}
          strategy={verticalListSortingStrategy}
        >
          {columnTasks.length > 0 ? (
            columnTasks.map((task) => (
              <TaskCard key={task.id} task={task} users={users} />
            ))
          ) : (
            <div
              className={`h-full min-h-[120px] flex flex-col items-center justify-center text-sm text-gray-400 border border-dashed border-[#2f3236]/50 rounded-lg p-4 ${
                isOver ? "bg-[#262b30]/70 border-[#4db1e0]/50 shadow-inner" : ""
              } ${isArchiveColumn ? "bg-[#262b30]/50" : ""}`}
            >
              <div className="text-2xl mb-2">
                {isArchiveColumn ? "🗃️" : "🔄"}
              </div>
              <p className="text-center">
                {isArchiveColumn ? "Archive is empty" : "Drop tasks here"}
              </p>
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
}
